import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import {
    CWTFieldReconciliation,
    CWTSchema,
    CWTSchemaCalculation,
    CWTSchemaField,
    CWTSchemaPropertiesSummary,
    CWTSchemaProperty,
    CWTSchemaPropertySummary,
    GeneratorFunction,
    FieldType,
    ListType,
    QuickAction,
    QuickActionType,
    SectionType,
    TableRowStyles,
    TableType,
} from '../../../types/custom-worksheet-v2';
import {
    addField,
    addList,
    addSection,
    addSectionGroup,
    buildReferenceMap,
    updatePropertyReference,
    getProperty,
    isCWTSchemaField,
    addTable,
    updateTableShowSummary,
    updateSectionSummary,
    unPinExistingFields,
    getToDeleteReferences,
    getProperties,
    isCWTSchemaTable,
    removeFieldFromSectionSummary,
    generateNewId,
    addFieldToSectionSummary,
    canSummarise,
    buildSummaryObject,
    getCurrentPinnedField,
    isCWTSchemaSection,
    isCWTSchemaList,
    buildPropertyPath,
    removePropertyFromListSummary,
    addPropertyToListSummary,
    updateListSummaryOnReferenceChange,
    updateListSummaryOnTypeChange,
    getToSummarisedField,
    reorderProperties,
    isInCollection,
} from './utils';
import { get, set, unset } from 'lodash';
import { formatMixedCase } from '../../../utilities/formatting';
import { NUMERIC_TYPES } from '../../../constants';
import { formatUniqueReference, getReference, hasRestrictedSymbols, isKeyword } from '../../../components/custom-worksheet/utils';
import traverse from 'traverse';
import { isField, isList, isTable } from '../../worksheet-v2/custom-worksheet-v2/store/utils';

export type ReferenceMap = { [key: string]: boolean };

export type CWTState = {
    /**
     * CWT schema value
     */
    value: CWTSchema;
    /**
     * List of reference keys in the template
     */
    referenceMap: ReferenceMap;
    /**
     * Property that's currently being renamed
     */
    renamingPropertyId?: string;
    /**
     * Property with description currently being updated
     */
    updatingDescriptionPropertyId?: string;
    /**
     * Field with reference field currently being updated
     */
    updatingReferenceFieldId?: string;
    /**
     * Field with type field currently being updated
     */
    updatingTypeFieldId?: string;
    /**
     * Property that's currently being focused
     */
    currentFocusPropertyId?: string;
};

const initialState: CWTState = {
    value: { version: '1', properties: {}, reviewConditions: [] },
    referenceMap: {},
};

// @ts-ignore
const schemaSlice = createSlice({
    name: 'custom-worksheet-template-schema',
    initialState,
    reducers: {
        loadSchema: (state: CWTState, action: PayloadAction<CWTSchema>) => {
            state.value = action.payload;
            // Build initial reference map
            state.referenceMap = buildReferenceMap(state.value);
        },

        setRenamingPropertyId: (state: CWTState, action: PayloadAction<string | undefined>) => {
            state.renamingPropertyId = action.payload;
        },

        setUpdatingDescriptionPropertyId: (state: CWTState, action: PayloadAction<string | undefined>) => {
            state.updatingDescriptionPropertyId = action.payload;
        },

        setUpdatingReferenceFieldId: (state: CWTState, action: PayloadAction<string | undefined>) => {
            state.updatingReferenceFieldId = action.payload;
        },

        setUpdatingTypeFieldId: (state: CWTState, action: PayloadAction<string | undefined>) => {
            state.updatingTypeFieldId = action.payload;
        },

        setCurrentFocusProperty: (state: CWTState, action: PayloadAction<string | undefined>) => {
            state.currentFocusPropertyId = action.payload;
        },

        removeProperty: (state: CWTState, action: PayloadAction<string>) => {
            const schema = traverse(state.value).clone();
            const property = getProperty(schema, action.payload);

            if (!property) {
                return;
            }

            unset(schema, action.payload);

            // Remove deleted property references from current reference map
            const deletedReferences = getToDeleteReferences(property);
            const currentReferences = state.referenceMap;

            deletedReferences.forEach((ref) => {
                delete currentReferences[ref];
            });

            state.referenceMap = currentReferences;

            // Reorder all other child properties when the deleted property is a property within a parent property
            const parentPropertiesId = action.payload.split('.').slice(0, -1).join('.');
            const parentProperties = getProperties(schema, parentPropertiesId);

            if (parentProperties) {
                set(schema, parentPropertiesId, reorderProperties(parentProperties));
            }

            const propertyPath = buildPropertyPath(state.value, property);

            // If deleted property is a field within a table/section summary, remove field from summary object
            if (isCWTSchemaField(property)) {
                const tableParent = propertyPath.find((p) => p.type === TableType.TABLE);
                if (tableParent && tableParent.summary) {
                    const currentSummary = tableParent.summary.properties;
                    delete currentSummary[getReference(action.payload)];
                    set(schema, `${tableParent.id}.summary.properties`, currentSummary);
                }

                const sectionParent = propertyPath.find((p) => p.type === SectionType.SECTION);
                if (sectionParent) {
                    const sectionSummary = sectionParent.summary?.properties[getReference(sectionParent.id)];

                    if (sectionSummary && sectionSummary.type === property.type) {
                        const newSummary = removeFieldFromSectionSummary(sectionSummary, action.payload);
                        set(schema, `${sectionParent.id}.summary`, { properties: { [getReference(sectionParent.id)]: newSummary } });
                    }
                }
            }

            const listParent = propertyPath.find((p) => p.type === ListType.LIST);
            if (listParent && listParent.summary && listParent.summary.properties[getReference(property.id)]) {
                const updatedSummary = removePropertyFromListSummary(listParent.summary, getReference(property.id));
                set(schema, `${listParent.id}.summary`, updatedSummary);
            }

            state.value = schema;
        },

        editPropertyName: (state: CWTState, action: PayloadAction<{ id: string; value: string }>) => {
            const referenceMap = state.referenceMap;
            const currentReference = getReference(action.payload.id);

            // Update current reference map with new reference value
            delete referenceMap[currentReference];
            const newUniqueReference = formatUniqueReference(action.payload.value, referenceMap);
            referenceMap[newUniqueReference] = true;

            // If new property reference equals to current property reference, only update the property field without having to update all the references used in ids
            if (currentReference === newUniqueReference) {
                set(state.value, `${action.payload.id}.name`, action.payload.value);
                return;
            }

            const property = getProperty(state.value, action.payload.id);

            if (!property) {
                return;
            }

            const newId = generateNewId(property.id, newUniqueReference);
            const updatedSchema = updatePropertyReference(state.value, property, newId, newUniqueReference);
            set(updatedSchema, `${newId}.name`, action.payload.value);

            state.value = updatedSchema;
            state.referenceMap = referenceMap;

            if (state.currentFocusPropertyId === action.payload.id) {
                state.currentFocusPropertyId = newId;
            }

            const updatedProperty = get(updatedSchema, newId);
            const listSummary = updateListSummaryOnReferenceChange(updatedSchema, updatedProperty, currentReference);

            if (listSummary !== undefined) {
                set(state.value, `${listSummary.listId}.summary`, listSummary.summary);
            }
        },

        editPropertyDetails: (state: CWTState, action: PayloadAction<{ id: string; value: string | null | FieldType[] }>) => {
            set(state.value, action.payload.id, action.payload.value);
        },

        editField: (
            state: CWTState,
            action: PayloadAction<{
                id: string;
                details: Partial<Omit<CWTSchemaField, 'type'> & { type: FieldType; styles: TableRowStyles }>;
            }>
        ) => {
            const field = getProperty(state.value, action.payload.id);

            if (!field || !isCWTSchemaField(field)) {
                return;
            }

            // Build updated field object with updated details
            const updatedField = {
                ...field,
                reconciliation:
                    action.payload.details.reconciliation !== undefined ? action.payload.details.reconciliation : field.reconciliation,
                name: action.payload.details.name !== undefined ? action.payload.details.name : field.name,
                type: action.payload.details.type !== undefined ? action.payload.details.type : field.type,
                pinned: action.payload.details.pinned !== undefined ? action.payload.details.pinned : field.pinned,
                hidden: action.payload.details.hidden !== undefined ? action.payload.details.hidden : field.hidden,
                defaultValue: action.payload.details.defaultValue !== undefined ? action.payload.details.defaultValue : field.defaultValue,
                helperText: action.payload.details.helperText !== undefined ? action.payload.details.helperText : field.helperText,
                canRename: action.payload.details.canRename !== undefined ? action.payload.details.canRename : field.canRename,
                canPin: action.payload.details.canRename !== undefined ? action.payload.details.canPin : field.canPin,
                styles:
                    'styles' in field
                        ? action.payload.details.styles !== undefined
                            ? action.payload.details.styles
                            : field.styles
                        : undefined,
            };

            // If field type value is updated, check if section/list summary needs to be updated too
            if (action.payload.details.type) {
                const parentId = action.payload.id.split('.').slice(0, -2).join('.');
                const parentProperty = getProperty(state.value, parentId);

                if (parentProperty && isCWTSchemaSection(parentProperty) && parentProperty.summary) {
                    let summary = parentProperty.summary.properties[getReference(parentId)];

                    if (summary) {
                        if (action.payload.details.type === summary.type) {
                            summary = addFieldToSectionSummary(summary, action.payload.id);
                        } else {
                            summary = removeFieldFromSectionSummary(summary, action.payload.id);
                        }

                        set(state.value, `${parentProperty.id}.summary`, {
                            properties: { [getReference(parentProperty.id)]: summary },
                        });
                    }
                }

                /**
                 * If field belongs in a summary, pass in section with updated field to update list summary
                 * If field does not belong in a summary, pass in actual updated field to update list summary
                 */

                const listSummary = updateListSummaryOnTypeChange(
                    state.value,
                    parentProperty && isCWTSchemaSection(parentProperty)
                        ? {
                              ...parentProperty,
                              properties: {
                                  ...parentProperty.properties,
                                  [getReference(field.id)]: { ...field, type: action.payload.details.type },
                              },
                          }
                        : { ...field, type: action.payload.details.type }
                );

                if (listSummary !== undefined) {
                    set(state.value, `${listSummary.listId}.summary`, listSummary.summary);
                }
            }

            // If new field is being pinned, ensure existing pinned field is unpinned
            if (action.payload.details.pinned) {
                const parentId = action.payload.id.split('.').slice(0, -2).join('.');
                const parentProperty = getProperty(state.value, parentId);

                if (!parentProperty || !isCWTSchemaSection(parentProperty)) {
                    return;
                }

                const currentPinnedFieldId = getCurrentPinnedField(parentProperty);

                if (currentPinnedFieldId) {
                    set(state.value, `${currentPinnedFieldId}.pinned`, false);
                }
            }

            // If field isPinned details is updated, check if list summary needs to be updated
            if (action.payload.details.pinned !== undefined) {
                const propertyPath = buildPropertyPath(state.value, field);
                const listParent = propertyPath.find((p) => p.type === ListType.LIST);
                const sectionParent = propertyPath.find((p) => p.type === SectionType.SECTION);

                // If field belongs in a section and list summary, update list summary with updated field or remove field from list summary
                if (listParent && listParent.summary && sectionParent && listParent.summary.properties[getReference(sectionParent.id)]) {
                    let updatedSummary: CWTSchemaPropertiesSummary | null = listParent.summary;

                    const prop = getProperty(state.value, updatedField.id) as CWTSchemaProperty;

                    if (action.payload.details.pinned && NUMERIC_TYPES.includes(updatedField.type)) {
                        updatedSummary = addPropertyToListSummary({
                            list: listParent,
                            summaryReference: getReference(sectionParent.id),
                            summaryName: updatedField.name,
                            property: prop,
                        });
                    } else {
                        updatedSummary = removePropertyFromListSummary(listParent.summary, getReference(sectionParent.id));
                    }
                    set(state.value, `${listParent.id}.summary`, updatedSummary);
                }
            }

            set(state.value, action.payload.id, updatedField);
        },

        addProperty: (
            state: CWTState,
            action: PayloadAction<{ type: 'LIST' | 'SECTION_GROUP' | 'SECTION' | 'FIELD' | 'TABLE' | 'TABLE_COLUMN'; parentId?: string }>
        ) => {
            const { type, parentId } = action.payload;

            // New table row property must belong in a parent table property
            if (type === 'TABLE_COLUMN' && !parentId) {
                return;
            }

            let properties: { [key: string]: CWTSchemaProperty } = state.value.properties;
            let parentProperty: CWTSchemaProperty | null = null;

            // If parentId is provided, get total number of properties within parent property
            if (parentId) {
                parentProperty = getProperty(state.value, type === 'TABLE_COLUMN' ? `${parentId}.properties.row` : parentId);

                // Ensure that parent property exists
                if (!parentProperty || isCWTSchemaField(parentProperty)) {
                    return;
                }

                properties = parentProperty.properties;
            }

            const newIndex = Object.keys(properties).length + 1;

            // Build property name using property type and new position index
            const name = type === 'TABLE_COLUMN' ? `Column ${newIndex}` : `${formatMixedCase(type)} ${newIndex}`;
            const newUniqueReference = formatUniqueReference(name, state.referenceMap);

            let newPropertyId = '';

            // Add property into appropriate group and save new property id
            switch (type) {
                case 'LIST': {
                    const newId = `properties.${newUniqueReference}`;
                    const newList = addList(newId, name, newIndex);
                    newPropertyId = newList.id;

                    set(state.value, newId, newList);
                    break;
                }
                case 'SECTION_GROUP': {
                    const newId = parentId ? `${parentId}.properties.${newUniqueReference}` : `properties.${newUniqueReference}`;
                    const newSectionGroup = addSectionGroup(newId, name, newIndex);
                    newPropertyId = newSectionGroup.id;

                    set(state.value, newId, newSectionGroup);
                    break;
                }
                case 'SECTION': {
                    const newId = parentId ? `${parentId}.properties.${newUniqueReference}` : `properties.${newUniqueReference}`;
                    const newSection = addSection(newId, name, newIndex);
                    newPropertyId = newSection.id;

                    set(state.value, newId, newSection);
                    break;
                }
                case 'FIELD': {
                    const schema = traverse(state.value).clone();
                    const newId = parentId ? `${parentId}.properties.${newUniqueReference}` : `properties.${newUniqueReference}`;

                    let newField = addField(newId, name, newIndex);
                    newPropertyId = newField.id;

                    // If field is added to a section property, check if section summary type is the same as field type
                    if (parentProperty && isCWTSchemaSection(parentProperty) && parentProperty.summary) {
                        const summary = parentProperty.summary.properties[getReference(parentProperty.id)];

                        if (summary && summary.type === newField.type) {
                            const newSectionSummary = addFieldToSectionSummary(summary, newField.id);
                            newField = {
                                ...newField,
                                dependentFields: [{ ref: `#${parentProperty.id}.summary.properties.${getReference(parentProperty.id)}` }],
                            };

                            set(schema, `${parentProperty.id}.summary`, {
                                properties: { [getReference(parentProperty.id)]: newSectionSummary },
                            });
                        }
                    }

                    set(schema, newId, newField);
                    state.value = schema;

                    break;
                }
                case 'TABLE': {
                    const newId = parentId ? `${parentId}.properties.${newUniqueReference}` : `properties.${newUniqueReference}`;
                    const newTable = addTable(newId, name, newIndex);
                    newPropertyId = newTable.id;

                    set(state.value, newId, newTable);
                    break;
                }
                case 'TABLE_COLUMN': {
                    const schema = traverse(state.value).clone();
                    const table = getProperty(schema, parentId as string);

                    // Ensure that table property exists
                    if (!table || !isCWTSchemaTable(table)) {
                        return;
                    }

                    const newId = `${parentId}.properties.row.properties.${newUniqueReference}`;
                    let newColumn = addField(newId, name, newIndex);
                    newPropertyId = newColumn.id;

                    const alignment = [FieldType.ACCOUNTING, FieldType.MONEY].includes(newColumn.type) ? 'right' : 'left';

                    if (table.summary) {
                        const currentSummary = table.summary.properties;

                        if (canSummarise(newColumn.type)) {
                            newColumn = {
                                ...newColumn,
                                dependentFields: [{ ref: `#${newColumn.id.replace('.properties.row.', '.summary.')}` }],
                            };
                        }

                        currentSummary[newUniqueReference] = buildSummaryObject(newColumn.id, newColumn.type, canSummarise(newColumn.type));

                        set(schema, `${table.id}.summary.properties`, currentSummary);
                    }

                    // Update schema table with new column
                    set(schema, `${table.id}.properties.row.properties.${newUniqueReference}`, {
                        ...newColumn,
                        styles: { width: '200px', alignment },
                    });

                    state.value = schema;
                    break;
                }
                default: {
                    return;
                }
            }

            // After adding new property, auto focus on input to allow updating property name
            state.renamingPropertyId = newPropertyId;

            // Add new property reference to current reference map
            state.referenceMap[newUniqueReference] = true;
        },

        editPropertyReference: (state: CWTState, action: PayloadAction<{ id: string; value: string }>) => {
            const newReference = action.payload.value;

            // Ensure that new field reference doesn't exist in the template
            if (state.referenceMap[newReference]) {
                return;
            }

            const property = getProperty(state.value, action.payload.id);

            if (!property) {
                return;
            }

            if (isKeyword(newReference) || hasRestrictedSymbols(newReference)) {
                return;
            }

            const newId = generateNewId(property.id, newReference);
            const updatedSchema = updatePropertyReference(state.value, property, newId, newReference);
            state.value = updatedSchema;

            const referenceMap = state.referenceMap;
            const currentReference = getReference(action.payload.id);

            // Update current reference map with new reference value
            delete referenceMap[currentReference];
            referenceMap[newReference] = true;
            state.referenceMap = referenceMap;

            if (state.currentFocusPropertyId === action.payload.id) {
                state.currentFocusPropertyId = newId;
            }

            const updatedProperty = get(updatedSchema, newId);
            const listSummary = updateListSummaryOnReferenceChange(state.value, updatedProperty, currentReference);

            if (listSummary !== undefined) {
                set(state.value, `${listSummary.listId}.summary`, listSummary.summary);
            }
        },

        editCalculation: (
            state: CWTState,
            action: PayloadAction<{ id: string; value: CWTSchemaCalculation | GeneratorFunction | null }>
        ) => {
            const prop = get(state.value, action.payload.id);
            if (isTable(prop)) {
                set(state.value, `${action.payload.id}.generatedFromFunction`, action.payload.value);
                set(state.value, `${action.payload.id}.readOnly`, action.payload.value !== null);
            } else {
                set(state.value, `${action.payload.id}.calculation`, action.payload.value);
                set(state.value, `${action.payload.id}.readOnly`, action.payload.value !== null);
            }
        },

        editTableShowSummary: (state: CWTState, action: PayloadAction<string>) => {
            const table = getProperty(state.value, action.payload);

            if (!table || !isCWTSchemaTable(table)) {
                return;
            }

            const tableSummary = updateTableShowSummary(table);
            set(state.value, `${action.payload}.summary`, tableSummary);
        },

        editTableConfig: (state: CWTState, action: PayloadAction<{ id: string; minRows: number }>) => {
            set(state.value, `${action.payload.id}.config.minRows`, action.payload.minRows);
        },

        editTableColumnSummary: (
            state: CWTState,
            action: PayloadAction<{ id: string; value: { enabled?: boolean; reconciliation?: CWTFieldReconciliation | null } }>
        ) => {
            const columnId = action.payload.id;
            const enableSummary = action.payload.value.enabled;
            const reconciliation = action.payload.value.reconciliation;
            const column = getProperty(state.value, columnId) as CWTSchemaField;

            if (!column) {
                return;
            }

            const table = isInCollection(state.value, column);

            if (!table || !isCWTSchemaTable(table) || !table.summary) {
                return;
            }

            const tableSummary = table.summary.properties;
            const currentSummary = table.summary.properties[columnId.split('.').at(-1) as string];

            const summary = buildSummaryObject(
                columnId,
                column.type,
                enableSummary ?? Boolean(currentSummary),
                reconciliation === undefined
                    ? currentSummary?.reconciliation
                    : reconciliation === null
                    ? null
                    : // Default the reconciliation description to the column name. Summary names are set to 'Total' and will be vague to the client.
                      { ...reconciliation, defaultDescription: reconciliation.defaultDescription ?? column.name }
            );

            if (summary !== null) {
                tableSummary[getReference(columnId)] = summary;
            } else {
                delete tableSummary[getReference(columnId)];
            }

            set(state.value, `${table.id}.summary.properties`, tableSummary);
        },

        editSectionShowSummary: (state: CWTState, action: PayloadAction<{ id: string; type?: FieldType }>) => {
            const section = getProperty(state.value, action.payload.id);

            if (!section || !isCWTSchemaSection(section)) {
                return;
            }

            const summary = updateSectionSummary(section, action.payload.type);

            // When summary is set, ensure all pinned fields are unpinned
            const sectionProperties = unPinExistingFields(section);

            set(state.value, section.id, {
                ...section,
                summary: summary === null ? null : { properties: { [getReference(section.id)]: summary } },
                properties: sectionProperties,
            });

            // If section summary type is updated, update list summary if necessary
            if (action.payload.type) {
                const listSummary = updateListSummaryOnTypeChange(state.value, {
                    ...section,
                    summary: summary === null ? null : { properties: { [getReference(section.id)]: summary } },
                    properties: sectionProperties,
                });

                if (listSummary !== undefined) {
                    set(state.value, `${listSummary.listId}.summary`, listSummary.summary);
                }
            } else {
                const propertyPath = buildPropertyPath(state.value, section);
                const listParent = propertyPath.find((p) => p.type === ListType.LIST);

                // If section belongs in a summary, check if section is included in list summary
                if (listParent && listParent.summary && listParent.summary.properties[getReference(section.id)]) {
                    const updatedSummary = removePropertyFromListSummary(listParent.summary, getReference(section.id));
                    set(state.value, `${listParent.id}.summary`, updatedSummary);
                }
            }
        },

        editListSummary: (state: CWTState, action: PayloadAction<{ id: string; summarisedPropertyId: string }>) => {
            const list = getProperty(state.value, action.payload.id);

            if (!list || !isCWTSchemaList(list)) {
                return;
            }

            const summarisedPropertyReference = getReference(action.payload.summarisedPropertyId);

            // If summarisedProperty is found in the currently list summary, it means that the summarisedProperty is being toggled off
            if (list.summary && list.summary.properties[summarisedPropertyReference]) {
                const updatedSummary = removePropertyFromListSummary(list.summary, summarisedPropertyReference);
                set(state.value, `${action.payload.id}.summary`, updatedSummary);

                return;
            }

            const summarisedProperty = getProperty(state.value, action.payload.summarisedPropertyId);

            if (!summarisedProperty || (!isCWTSchemaSection(summarisedProperty) && !isCWTSchemaField(summarisedProperty))) {
                return;
            }

            // Get the actual field to be added to the list summary (section summary value OR section pinned field OR field)
            const summarisedField = getToSummarisedField(summarisedProperty);

            if (!summarisedField) {
                return;
            }

            const property = getProperty(state.value, summarisedField.id) as CWTSchemaProperty;

            const updatedSummary = addPropertyToListSummary({
                list,
                summaryReference: getReference(summarisedProperty.id),
                summaryName: summarisedProperty.name,
                property,
            });

            set(state.value, `${action.payload.id}.summary`, updatedSummary);
        },

        addQuickAction(state: CWTState, action: PayloadAction<{ prop: CWTSchemaField | CWTSchemaPropertySummary; action: QuickAction }>) {
            set(state.value, `${action.payload.prop.id}.quickActions`, [
                ...(action.payload.prop.quickActions ?? []),
                action.payload.action,
            ]);

            // If this is in a list, check if it's also summaries
            const collection = isInCollection(state.value, action.payload.prop, true);

            if (!collection || collection.type !== ListType.LIST) {
                return;
            }

            if (!collection.summary) {
                return;
            }

            const propertyId = action.payload.prop.id.split('.').at(-1) as string;
            const summary = collection?.summary?.properties?.[propertyId];

            if (summary) {
                set(state.value, `${summary.id}.quickActions`, [...(action.payload.prop.quickActions ?? []), action.payload.action]);
            }
        },

        removeQuickAction(
            state: CWTState,
            action: PayloadAction<{ prop: CWTSchemaField | CWTSchemaPropertySummary; type: QuickActionType }>
        ) {
            if (action.payload.prop.quickActions) {
                set(
                    state.value,
                    `${action.payload.prop.id}.quickActions`,
                    action.payload.prop.quickActions?.filter((qa) => qa?.type !== action.payload.type)
                );

                // If this is in a list, check if it's also summaries
                const collection = isInCollection(state.value, action.payload.prop, true);

                if (!collection || collection.type !== ListType.LIST) {
                    return;
                }

                if (!collection.summary) {
                    return;
                }

                const propertyId = action.payload.prop.id.split('.').at(-1) as string;
                const summary = collection?.summary?.properties?.[propertyId];
                if (summary) {
                    set(
                        state.value,
                        `${summary.id}.quickActions`,
                        action.payload.prop.quickActions?.filter((qa) => qa?.type !== action.payload.type)
                    );
                }
            }
        },

        editQuickAction(state: CWTState, action: PayloadAction<{ id: string; type: QuickActionType; payload: unknown }>) {
            const property = getProperty(state.value, action.payload.id);

            if (!property) {
                return;
            }

            if (!isCWTSchemaField(property)) {
                return;
            }

            // Find a matching quick action
            const quickAction = property.quickActions?.findIndex((qa) => qa?.type === action.payload.type);
            if (quickAction === undefined || quickAction === -1) {
                return;
            }
            set(state.value, `${action.payload.id}.quickActions.${quickAction}.payload`, action.payload.payload);

            // If this is in a list, check if it's also summaries
            const collection = isInCollection(state.value, property, true);

            if (!collection || collection.type !== ListType.LIST) {
                return;
            }

            if (!collection.summary) {
                return;
            }

            const propertyId = action.payload.id.split('.').at(-1) as string;
            const summary = collection?.summary?.properties?.[propertyId];
            if (summary) {
                set(state.value, `${summary.id}.quickActions.${quickAction}.payload`, action.payload.payload);
            }
        },
    },
});

export const {
    loadSchema,
    setRenamingPropertyId,
    setUpdatingDescriptionPropertyId,
    setUpdatingReferenceFieldId,
    setUpdatingTypeFieldId,
    setCurrentFocusProperty,
    editPropertyName,
    editPropertyDetails,
    editField,
    removeProperty,
    addProperty,
    editPropertyReference,
    editCalculation,
    editTableShowSummary,
    editTableColumnSummary,
    editSectionShowSummary,
    editTableConfig,
    editListSummary,
    removeQuickAction,
    addQuickAction,
    editQuickAction,
} = schemaSlice.actions;
export default schemaSlice.reducer;
